import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaShieldAlt, FaUsers, FaTicketAlt, FaBuilding, FaChartLine,
  FaPlus, FaKey, FaSignOutAlt, FaCopy, FaTrash, FaEye, FaSync,
  FaUserShield, FaCog, FaUserPlus, FaEdit, FaDownload, FaCheckSquare
} from 'react-icons/fa';
import { API_BASE_URL } from '../../config/api';
import './SuperAdminDashboard.css';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgPerformance, setOrgPerformance] = useState(null);

  const [newUserForm, setNewUserForm] = useState({
    email: '',
    displayName: '',
    organisation: '',
    subscriberType: 'authority',
    territory: ''
  });

  const [newAdminForm, setNewAdminForm] = useState({
    email: '',
    name: '',
    permissions: {
      viewStats: false,
      viewSubscribers: false,
      manageCoupons: false,
      addCorporateUsers: false,
      resetPasswords: false
    }
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [selectedCoupons, setSelectedCoupons] = useState([]);

  const [couponForm, setCouponForm] = useState({
    durationMonths: 3,
    discountPercent: 25,
    quantity: 1
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('superAdminToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/stats`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Stats error:', error);
    }
  }, [getAuthHeaders]);

  const fetchSubscribers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/subscribers`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch subscribers');
      const data = await response.json();
      setSubscribers(data);
    } catch (error) {
      console.error('Subscribers error:', error);
    }
  }, [getAuthHeaders]);

  const fetchCoupons = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/coupons`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch coupons');
      const data = await response.json();
      setCoupons(data);
    } catch (error) {
      console.error('Coupons error:', error);
    }
  }, [getAuthHeaders]);

  const fetchAdmins = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/admins`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch admins');
      const data = await response.json();
      setAdmins(data.admins || []);
    } catch (error) {
      console.error('Admins error:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    const adminData = localStorage.getItem('superAdmin');
    
    if (!token) {
      navigate('/super-admin/login');
      return;
    }

    const parsedAdmin = JSON.parse(adminData);
    setAdmin(parsedAdmin);
    
    // Set default active tab based on permissions
    if (!parsedAdmin?.isSuperAdmin) {
      const perms = parsedAdmin?.permissions || {};
      if (perms.viewStats) setActiveTab('overview');
      else if (perms.viewSubscribers) setActiveTab('subscribers');
      else if (perms.addCorporateUsers) setActiveTab('add-user');
      else if (perms.manageCoupons) setActiveTab('coupons');
      else setActiveTab('settings');
    }
    
    if (parsedAdmin?.isSuperAdmin) {
      Promise.all([fetchStats(), fetchSubscribers(), fetchCoupons(), fetchAdmins()])
        .finally(() => setLoading(false));
    } else {
      // Only fetch what the admin has permission for
      const fetches = [];
      if (parsedAdmin?.permissions?.viewStats) fetches.push(fetchStats());
      if (parsedAdmin?.permissions?.viewSubscribers) fetches.push(fetchSubscribers());
      if (parsedAdmin?.permissions?.manageCoupons) fetches.push(fetchCoupons());
      Promise.all(fetches).finally(() => setLoading(false));
    }
  }, [navigate, fetchStats, fetchSubscribers, fetchCoupons, fetchAdmins]);

  const handleLogout = () => {
    localStorage.removeItem('superAdminToken');
    localStorage.removeItem('superAdmin');
    navigate('/super-admin/login');
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/add-corporate-user`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newUserForm)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      
      toast.success(`User created! ${data.tempPassword ? `Temp password: ${data.tempPassword}` : 'Email sent with credentials.'}`);
      setNewUserForm({ email: '', displayName: '', organisation: '', subscriberType: 'authority', territory: '' });
      fetchSubscribers();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResetPassword = async (uid) => {
    if (!window.confirm('Reset password for this user?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/reset-password/${uid}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      
      toast.success(`Password reset! ${data.newPassword ? `New password: ${data.newPassword}` : 'Email sent.'}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleGenerateCoupons = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/coupons/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(couponForm)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      
      toast.success(`Generated ${data.coupons.length} coupon(s)`);
      fetchCoupons();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteCoupon = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/coupons/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success('Coupon deleted');
      fetchCoupons();
      fetchStats();
      setSelectedCoupons(prev => prev.filter(id => id !== id));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const toggleSelectCoupon = (couponId) => {
    setSelectedCoupons(prev => 
      prev.includes(couponId) 
        ? prev.filter(id => id !== couponId)
        : [...prev, couponId]
    );
  };

  const toggleSelectAllCoupons = () => {
    if (selectedCoupons.length === coupons.length) {
      setSelectedCoupons([]);
    } else {
      setSelectedCoupons(coupons.map(c => c.id));
    }
  };

  const handleDeleteSelectedCoupons = async () => {
    if (selectedCoupons.length === 0) {
      toast.error('No coupons selected');
      return;
    }
    if (!window.confirm(`Delete ${selectedCoupons.length} selected coupon(s)?`)) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/coupons/bulk-delete`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ couponIds: selectedCoupons })
      });
      if (!response.ok) throw new Error('Failed to delete coupons');
      
      toast.success(`Deleted ${selectedCoupons.length} coupon(s)`);
      setSelectedCoupons([]);
      fetchCoupons();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDownloadSelectedCoupons = () => {
    if (selectedCoupons.length === 0) {
      toast.error('No coupons selected');
      return;
    }
    
    const selectedData = coupons.filter(c => selectedCoupons.includes(c.id));
    
    const headers = ['Code', 'Duration (Months)', 'Discount (%)', 'Status', 'Used By', 'Created At'];
    const rows = selectedData.map(c => [
      c.code,
      c.duration_months,
      c.discount_percent,
      c.used ? 'Used' : 'Available',
      c.used_by || '',
      new Date(c.created_at).toLocaleDateString()
    ]);
    
    let csvContent = headers.join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
    });
    
    const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `coupons_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success(`Downloaded ${selectedCoupons.length} coupon(s)`);
  };

  const copyCoupon = (code) => {
    navigator.clipboard.writeText(code);
    toast.info(`Copied: ${code}`);
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/admins`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newAdminForm)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      
      toast.success(`Admin created! Temp password: ${data.tempPassword}`);
      setNewAdminForm({
        email: '',
        name: '',
        permissions: {
          viewStats: false,
          viewSubscribers: false,
          manageCoupons: false,
          addCorporateUsers: false,
          resetPasswords: false
        }
      });
      fetchAdmins();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteAdmin = async (id) => {
    if (!window.confirm('Delete this admin?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/admins/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success('Admin deleted');
      fetchAdmins();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResetAdminPassword = async (id) => {
    if (!window.confirm('Reset password for this admin?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/admins/${id}/reset-password`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      
      toast.success(`Password reset! New password: ${data.tempPassword}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const viewOrgPerformance = async (org) => {
    setSelectedOrg(org);
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/organisation/${org.uid}/performance`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setOrgPerformance(data);
    } catch (error) {
      toast.error('Failed to load performance data');
    }
  };

  const getOrgTypeLabel = (type) => {
    const labels = {
      'authority': 'Local Authority',
      'private_company': 'Private Waste Company',
      'recycling': 'Waste2Wealth Recycling'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="super-admin-loading">
        <FaShieldAlt size={48} />
        <p>Loading Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="super-admin-dashboard">
      <header className="sa-header">
        <div className="sa-header-left">
          <FaShieldAlt size={24} />
          <h1>Super Admin Dashboard</h1>
        </div>
        <div className="sa-header-right">
          <span>Welcome, {admin?.name}</span>
          <button onClick={handleLogout} className="sa-logout-btn">
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </header>

      <div className="sa-content">
        <nav className="sa-sidebar">
          {(admin?.isSuperAdmin || admin?.permissions?.viewStats) && (
            <button 
              className={`sa-nav-item ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <FaChartLine /> Overview
            </button>
          )}
          {(admin?.isSuperAdmin || admin?.permissions?.viewSubscribers) && (
            <button 
              className={`sa-nav-item ${activeTab === 'subscribers' ? 'active' : ''}`}
              onClick={() => setActiveTab('subscribers')}
            >
              <FaBuilding /> Subscribers
            </button>
          )}
          {(admin?.isSuperAdmin || admin?.permissions?.addCorporateUsers) && (
            <button 
              className={`sa-nav-item ${activeTab === 'add-user' ? 'active' : ''}`}
              onClick={() => setActiveTab('add-user')}
            >
              <FaPlus /> Add Corporate User
            </button>
          )}
          {(admin?.isSuperAdmin || admin?.permissions?.manageCoupons) && (
            <button 
              className={`sa-nav-item ${activeTab === 'coupons' ? 'active' : ''}`}
              onClick={() => setActiveTab('coupons')}
            >
              <FaTicketAlt /> Coupon Codes
            </button>
          )}
          {admin?.isSuperAdmin && (
            <button 
              className={`sa-nav-item ${activeTab === 'admins' ? 'active' : ''}`}
              onClick={() => setActiveTab('admins')}
            >
              <FaUserShield /> Manage Admins
            </button>
          )}
          <button 
            className={`sa-nav-item ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <FaCog /> Settings
          </button>
        </nav>

        <main className="sa-main">
          {activeTab === 'overview' && (
            <div className="sa-overview">
              <h2>Dashboard Overview</h2>
              <div className="sa-stats-grid">
                <div className="sa-stat-card">
                  <FaUsers size={32} />
                  <div className="sa-stat-value">{stats?.totalSubscribers || 0}</div>
                  <div className="sa-stat-label">Total Subscribers</div>
                </div>
                <div className="sa-stat-card">
                  <FaBuilding size={32} />
                  <div className="sa-stat-value">{stats?.byType?.authority || 0}</div>
                  <div className="sa-stat-label">Local Authorities</div>
                </div>
                <div className="sa-stat-card">
                  <FaBuilding size={32} />
                  <div className="sa-stat-value">{stats?.byType?.private_company || 0}</div>
                  <div className="sa-stat-label">Private Companies</div>
                </div>
                <div className="sa-stat-card">
                  <FaBuilding size={32} />
                  <div className="sa-stat-value">{stats?.byType?.recycling || 0}</div>
                  <div className="sa-stat-label">Recycling Businesses</div>
                </div>
                <div className="sa-stat-card">
                  <FaTicketAlt size={32} />
                  <div className="sa-stat-value">{stats?.coupons?.available || 0}</div>
                  <div className="sa-stat-label">Available Coupons</div>
                </div>
                <div className="sa-stat-card">
                  <FaChartLine size={32} />
                  <div className="sa-stat-value">{stats?.recentSignups || 0}</div>
                  <div className="sa-stat-label">New (30 days)</div>
                </div>
              </div>

              <div className="sa-subscription-status">
                <h3>Subscription Status</h3>
                <div className="sa-status-bars">
                  <div className="sa-status-item">
                    <span>Active</span>
                    <div className="sa-status-bar">
                      <div 
                        className="sa-status-fill active"
                        style={{ width: `${stats?.totalSubscribers ? (stats?.subscriptionStatus?.active || 0) / stats.totalSubscribers * 100 : 0}%` }}
                      ></div>
                    </div>
                    <span>{stats?.subscriptionStatus?.active || 0}</span>
                  </div>
                  <div className="sa-status-item">
                    <span>Inactive</span>
                    <div className="sa-status-bar">
                      <div 
                        className="sa-status-fill inactive"
                        style={{ width: `${stats?.totalSubscribers ? (stats?.subscriptionStatus?.inactive || 0) / stats.totalSubscribers * 100 : 0}%` }}
                      ></div>
                    </div>
                    <span>{stats?.subscriptionStatus?.inactive || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'subscribers' && (
            <div className="sa-subscribers">
              <div className="sa-section-header">
                <h2>Corporate Subscribers</h2>
                <button onClick={() => { fetchSubscribers(); fetchStats(); }} className="sa-refresh-btn">
                  <FaSync /> Refresh
                </button>
              </div>
              
              <div className="sa-table-container">
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th>Organisation</th>
                      <th>Email</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Team</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map(sub => (
                      <tr key={sub.uid}>
                        <td>{sub.organisation || sub.displayName}</td>
                        <td>{sub.email}</td>
                        <td>{getOrgTypeLabel(sub.subscription?.type)}</td>
                        <td>
                          <span className={`sa-status-badge ${sub.subscription?.active ? 'active' : 'inactive'}`}>
                            {sub.subscription?.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{sub.soleAdministrator ? 'Solo' : `${sub.teamCount + 1} members`}</td>
                        <td>{new Date(sub.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button 
                            className="sa-action-btn" 
                            onClick={() => viewOrgPerformance(sub)}
                            title="View Performance"
                          >
                            <FaEye />
                          </button>
                          <button 
                            className="sa-action-btn warning" 
                            onClick={() => handleResetPassword(sub.uid)}
                            title="Reset Password"
                          >
                            <FaKey />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedOrg && orgPerformance && (
                <div className="sa-modal-overlay" onClick={() => setSelectedOrg(null)}>
                  <div className="sa-modal" onClick={e => e.stopPropagation()}>
                    <h3>Organisation Performance: {orgPerformance.organisation?.name}</h3>
                    <div className="sa-performance-grid">
                      <div className="sa-perf-item">
                        <label>Type</label>
                        <span>{getOrgTypeLabel(orgPerformance.organisation?.type)}</span>
                      </div>
                      <div className="sa-perf-item">
                        <label>Territory</label>
                        <span>{orgPerformance.organisation?.territory || 'Not assigned'}</span>
                      </div>
                      <div className="sa-perf-item">
                        <label>Status</label>
                        <span>{orgPerformance.organisation?.active ? 'Active' : 'Inactive'}</span>
                      </div>
                      <div className="sa-perf-item">
                        <label>Team Members</label>
                        <span>{orgPerformance.metrics?.teamMembers}</span>
                      </div>
                      <div className="sa-perf-item">
                        <label>Total Reports in Territory</label>
                        <span>{orgPerformance.metrics?.wasteReports?.total || 0}</span>
                      </div>
                      <div className="sa-perf-item">
                        <label>Pending Reports</label>
                        <span>{orgPerformance.metrics?.wasteReports?.pending || 0}</span>
                      </div>
                      <div className="sa-perf-item">
                        <label>Cleaned Reports</label>
                        <span>{orgPerformance.metrics?.wasteReports?.cleaned || 0}</span>
                      </div>
                      <div className="sa-perf-item">
                        <label>Crews</label>
                        <span>{orgPerformance.metrics?.crews || 0}</span>
                      </div>
                      <div className="sa-perf-item">
                        <label>Jobs Completed</label>
                        <span>{orgPerformance.metrics?.jobs?.completed || 0} / {orgPerformance.metrics?.jobs?.total || 0}</span>
                      </div>
                    </div>
                    <button className="btn btn-secondary" onClick={() => setSelectedOrg(null)}>Close</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'add-user' && (
            <div className="sa-add-user">
              <h2>Add Corporate User</h2>
              <form onSubmit={handleAddUser} className="sa-form">
                <div className="sa-form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newUserForm.email}
                    onChange={e => setNewUserForm({...newUserForm, email: e.target.value})}
                    required
                  />
                </div>
                <div className="sa-form-group">
                  <label>Display Name *</label>
                  <input
                    type="text"
                    value={newUserForm.displayName}
                    onChange={e => setNewUserForm({...newUserForm, displayName: e.target.value})}
                    required
                  />
                </div>
                <div className="sa-form-group">
                  <label>Organisation Name *</label>
                  <input
                    type="text"
                    value={newUserForm.organisation}
                    onChange={e => setNewUserForm({...newUserForm, organisation: e.target.value})}
                    required
                  />
                </div>
                <div className="sa-form-group">
                  <label>Organisation Type *</label>
                  <select
                    value={newUserForm.subscriberType}
                    onChange={e => setNewUserForm({...newUserForm, subscriberType: e.target.value})}
                  >
                    <option value="authority">Local Authority</option>
                    <option value="private_company">Private Waste Company</option>
                    <option value="recycling">Waste2Wealth Recycling</option>
                  </select>
                </div>
                <div className="sa-form-group">
                  <label>Assigned Territory (Optional)</label>
                  <input
                    type="text"
                    value={newUserForm.territory}
                    onChange={e => setNewUserForm({...newUserForm, territory: e.target.value})}
                    placeholder="e.g. Birmingham, Manchester"
                  />
                </div>
                <button type="submit" className="btn btn-primary">Create User</button>
                <p className="sa-form-note">A temporary password will be generated and emailed to the user.</p>
              </form>
            </div>
          )}

          {activeTab === 'coupons' && (
            <div className="sa-coupons">
              <h2>Coupon Code Management</h2>
              
              <div className="sa-coupon-generator">
                <h3>Generate New Coupons</h3>
                <form onSubmit={handleGenerateCoupons} className="sa-coupon-form">
                  <div className="sa-form-row">
                    <div className="sa-form-group">
                      <label>Duration</label>
                      <select
                        value={couponForm.durationMonths}
                        onChange={e => setCouponForm({...couponForm, durationMonths: parseInt(e.target.value)})}
                      >
                        <option value={3}>3 Months</option>
                        <option value={6}>6 Months</option>
                        <option value={12}>12 Months</option>
                      </select>
                    </div>
                    <div className="sa-form-group">
                      <label>Discount</label>
                      <select
                        value={couponForm.discountPercent}
                        onChange={e => setCouponForm({...couponForm, discountPercent: parseInt(e.target.value)})}
                      >
                        <option value={25}>25% Off</option>
                        <option value={50}>50% Off</option>
                        <option value={100}>100% Off (Free)</option>
                      </select>
                    </div>
                    <div className="sa-form-group">
                      <label>Quantity</label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={couponForm.quantity}
                        onChange={e => setCouponForm({...couponForm, quantity: parseInt(e.target.value)})}
                      />
                    </div>
                    <button type="submit" className="btn btn-primary">Generate</button>
                  </div>
                </form>
              </div>

              <div className="sa-coupon-list">
                <div className="sa-section-header">
                  <h3>All Coupons ({coupons.length})</h3>
                  <div className="sa-header-actions">
                    {selectedCoupons.length > 0 && (
                      <>
                        <button onClick={handleDeleteSelectedCoupons} className="sa-bulk-btn danger">
                          <FaTrash /> Delete Selected ({selectedCoupons.length})
                        </button>
                        <button onClick={handleDownloadSelectedCoupons} className="sa-bulk-btn">
                          <FaDownload /> Download Selected
                        </button>
                      </>
                    )}
                    <button onClick={fetchCoupons} className="sa-refresh-btn">
                      <FaSync /> Refresh
                    </button>
                  </div>
                </div>
                <div className="sa-table-container">
                  <table className="sa-table">
                    <thead>
                      <tr>
                        <th className="sa-checkbox-col">
                          <input 
                            type="checkbox" 
                            checked={coupons.length > 0 && selectedCoupons.length === coupons.length}
                            onChange={toggleSelectAllCoupons}
                            title="Select All"
                          />
                        </th>
                        <th>Code</th>
                        <th>Duration</th>
                        <th>Discount</th>
                        <th>Status</th>
                        <th>Used By</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coupons.map(coupon => (
                        <tr key={coupon.id} className={selectedCoupons.includes(coupon.id) ? 'selected' : ''}>
                          <td className="sa-checkbox-col">
                            <input 
                              type="checkbox" 
                              checked={selectedCoupons.includes(coupon.id)}
                              onChange={() => toggleSelectCoupon(coupon.id)}
                            />
                          </td>
                          <td className="sa-coupon-code">{coupon.code}</td>
                          <td>{coupon.duration_months} months</td>
                          <td>{coupon.discount_percent}%</td>
                          <td>
                            <span className={`sa-status-badge ${coupon.used ? 'used' : 'available'}`}>
                              {coupon.used ? 'Used' : 'Available'}
                            </span>
                          </td>
                          <td>{coupon.used_by || '-'}</td>
                          <td>{new Date(coupon.created_at).toLocaleDateString()}</td>
                          <td>
                            <button 
                              className="sa-action-btn" 
                              onClick={() => copyCoupon(coupon.code)}
                              title="Copy Code"
                            >
                              <FaCopy />
                            </button>
                            {!coupon.used && (
                              <button 
                                className="sa-action-btn danger" 
                                onClick={() => handleDeleteCoupon(coupon.id)}
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'admins' && admin?.isSuperAdmin && (
            <div className="sa-admins">
              <h2>Admin Management</h2>
              
              <div className="sa-add-admin-section">
                <h3><FaUserPlus /> Add New Admin</h3>
                <form onSubmit={handleAddAdmin} className="sa-form">
                  <div className="sa-form-row">
                    <div className="sa-form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        value={newAdminForm.email}
                        onChange={e => setNewAdminForm({...newAdminForm, email: e.target.value})}
                        required
                        placeholder="admin@example.com"
                      />
                    </div>
                    <div className="sa-form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        value={newAdminForm.name}
                        onChange={e => setNewAdminForm({...newAdminForm, name: e.target.value})}
                        required
                        placeholder="Full Name"
                      />
                    </div>
                  </div>
                  
                  <div className="sa-permissions-section">
                    <label className="sa-permissions-label">Permissions (none selected by default)</label>
                    <div className="sa-permissions-grid">
                      <label className="sa-checkbox-label">
                        <input
                          type="checkbox"
                          checked={newAdminForm.permissions.viewStats}
                          onChange={e => setNewAdminForm({
                            ...newAdminForm,
                            permissions: {...newAdminForm.permissions, viewStats: e.target.checked}
                          })}
                        />
                        <span>View Dashboard Stats</span>
                      </label>
                      <label className="sa-checkbox-label">
                        <input
                          type="checkbox"
                          checked={newAdminForm.permissions.viewSubscribers}
                          onChange={e => setNewAdminForm({
                            ...newAdminForm,
                            permissions: {...newAdminForm.permissions, viewSubscribers: e.target.checked}
                          })}
                        />
                        <span>View Subscribers</span>
                      </label>
                      <label className="sa-checkbox-label">
                        <input
                          type="checkbox"
                          checked={newAdminForm.permissions.manageCoupons}
                          onChange={e => setNewAdminForm({
                            ...newAdminForm,
                            permissions: {...newAdminForm.permissions, manageCoupons: e.target.checked}
                          })}
                        />
                        <span>Manage Coupons</span>
                      </label>
                      <label className="sa-checkbox-label">
                        <input
                          type="checkbox"
                          checked={newAdminForm.permissions.addCorporateUsers}
                          onChange={e => setNewAdminForm({
                            ...newAdminForm,
                            permissions: {...newAdminForm.permissions, addCorporateUsers: e.target.checked}
                          })}
                        />
                        <span>Add Corporate Users</span>
                      </label>
                      <label className="sa-checkbox-label">
                        <input
                          type="checkbox"
                          checked={newAdminForm.permissions.resetPasswords}
                          onChange={e => setNewAdminForm({
                            ...newAdminForm,
                            permissions: {...newAdminForm.permissions, resetPasswords: e.target.checked}
                          })}
                        />
                        <span>Reset User Passwords</span>
                      </label>
                    </div>
                  </div>
                  
                  <button type="submit" className="btn btn-primary">
                    <FaUserPlus /> Create Admin
                  </button>
                  <p className="sa-form-note">A temporary password will be generated and displayed. Share it securely with the admin.</p>
                </form>
              </div>

              <div className="sa-admins-list">
                <div className="sa-section-header">
                  <h3>All Admins ({admins.length})</h3>
                  <button onClick={fetchAdmins} className="sa-refresh-btn">
                    <FaSync /> Refresh
                  </button>
                </div>
                <div className="sa-table-container">
                  <table className="sa-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Permissions</th>
                        <th>Created</th>
                        <th>Last Login</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {admins.map(adm => (
                        <tr key={adm.id}>
                          <td>{adm.name}</td>
                          <td>{adm.email}</td>
                          <td className="sa-permissions-cell">
                            {adm.perm_view_stats && <span className="sa-perm-badge">Stats</span>}
                            {adm.perm_view_subscribers && <span className="sa-perm-badge">Subs</span>}
                            {adm.perm_manage_coupons && <span className="sa-perm-badge">Coupons</span>}
                            {adm.perm_add_corporate_users && <span className="sa-perm-badge">Add Users</span>}
                            {adm.perm_reset_passwords && <span className="sa-perm-badge">Reset PW</span>}
                            {!adm.perm_view_stats && !adm.perm_view_subscribers && !adm.perm_manage_coupons && 
                             !adm.perm_add_corporate_users && !adm.perm_reset_passwords && 
                             <span className="sa-no-perms">No permissions</span>}
                          </td>
                          <td>{new Date(adm.created_at).toLocaleDateString()}</td>
                          <td>{adm.last_login ? new Date(adm.last_login).toLocaleDateString() : 'Never'}</td>
                          <td>
                            <button 
                              className="sa-action-btn" 
                              onClick={() => handleResetAdminPassword(adm.id)}
                              title="Reset Password"
                            >
                              <FaKey />
                            </button>
                            <button 
                              className="sa-action-btn danger" 
                              onClick={() => handleDeleteAdmin(adm.id)}
                              title="Delete Admin"
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {admins.length === 0 && (
                        <tr>
                          <td colSpan="6" className="sa-empty-row">No admins created yet</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="sa-settings">
              <h2><FaCog /> Account Settings</h2>
              
              <div className="sa-settings-section">
                <h3>Your Details</h3>
                <div className="sa-details-display">
                  <p><strong>Name:</strong> {admin?.name}</p>
                  <p><strong>Email:</strong> {admin?.email}</p>
                  <p><strong>Role:</strong> {admin?.isSuperAdmin ? 'Super Admin' : 'Admin'}</p>
                </div>
              </div>

              <div className="sa-settings-section">
                <h3>Change Password</h3>
                <form onSubmit={handleChangePassword} className="sa-form sa-password-form">
                  <div className="sa-form-group">
                    <label>Current Password *</label>
                    <input
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={e => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                      required
                    />
                  </div>
                  <div className="sa-form-group">
                    <label>New Password *</label>
                    <input
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={e => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                      required
                      minLength={8}
                    />
                  </div>
                  <div className="sa-form-group">
                    <label>Confirm New Password *</label>
                    <input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={e => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary">Change Password</button>
                </form>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
